import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { getApiData as getCompanyDataApi } from 'services/CompanyService'
import { getApiData as getBranchDataApi } from 'services/BranchService'
import { getApiData as getRoleDataApi } from 'services/RoleService'
import { getApiData as getAddonDataApi } from 'services/ProductService'
import { getApiPermissionData } from 'services/PermissionService'
import { actionType, moduleName, title } from 'components/helper'
import { moduleList } from 'utils/options'

export const getCompanyData = createAsyncThunk('listdata/getCompanyData', async () => {
    const response = await getCompanyDataApi()
    return response.data.results
        ?.map(item => {
            item.label = item.name
            item.value = item.id
            return item
        })
        .filter(item => item.status === 'active')
})

export const getBranchData = createAsyncThunk('listdata/getBranchData', async () => {
    const response = await getBranchDataApi()
    return response.data.results
        ?.map(item => {
            item.label = item.name
            item.value = item.id
            return item
        })
        .filter(item => item.status === 'active')
})

export const getRoleData = createAsyncThunk('listdata/getRoleData', async () => {
    const response = await getRoleDataApi()
    return response.data.results?.map(item => {
        item.label = item.name
        item.value = item.id
        return item
    })
})

export const getAddonData = createAsyncThunk('listdata/getAddonData', async () => {
    const response = await getAddonDataApi({ addon: true })
    return response.data.results?.map(item => {
        item.label = item.name
        item.value = item.id
        return item
    })
})

export const getPermissionData = createAsyncThunk('listdata/getPermissionData', async () => {
    const response = await getApiPermissionData()
    const moduleData = response.data
        .filter(item => !item.name.includes('delete'))
        .filter(item => {
            let name = item.name.split(' ')
            name.splice(0, 2)
            name = name.join(' ')
            return moduleList.find(value => value.key === name)
        })
        .map(item => {
            let nameArr = item.name.split(' ')
            nameArr.splice(0, 2)
            nameArr = nameArr.join(' ')
            item.module = moduleName(nameArr)
            item.action = actionType(item.name)
            item.title = title(item.name)
            return item
        })
    return Object.groupBy(moduleData, ({ module }) => module)
})

const dataSlice = createSlice({
    name: 'listdata',
    initialState: {
        companyData: [],
        branchData: [],
        roleData: [],
        addonData: [],
        permissionList: [],
    },
    reducers: {
        setCompanyData: (state, { payload }) => {
            state.companyData = payload
        },
        setBranchData: (state, { payload }) => {
            state.branchData = payload
        },
        setRoleData: (state, { payload }) => {
            state.roleData = payload
        },
        setAddonData: (state, { payload }) => {
            state.addonData = payload
        },
    },
    extraReducers: builder => {
        builder
            //company data
            .addCase(getCompanyData.pending, state => {
                state.loading = true
            })
            .addCase(getCompanyData.fulfilled, (state, action) => {
                state.loading = false
                state.companyData = action.payload
            })
            .addCase(getCompanyData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //branch data
            .addCase(getBranchData.pending, state => {
                state.loading = true
            })
            .addCase(getBranchData.fulfilled, (state, action) => {
                state.loading = false
                state.branchData = action.payload
            })
            .addCase(getBranchData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //role data
            .addCase(getRoleData.pending, state => {
                state.loading = true
            })
            .addCase(getRoleData.fulfilled, (state, action) => {
                state.loading = false
                state.roleData = action.payload
            })
            .addCase(getRoleData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //addon data
            .addCase(getAddonData.pending, state => {
                state.loading = true
            })
            .addCase(getAddonData.fulfilled, (state, action) => {
                state.loading = false
                state.addonData = action.payload
            })
            .addCase(getAddonData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            //permission data
            .addCase(getPermissionData.pending, state => {
                state.loading = true
            })
            .addCase(getPermissionData.fulfilled, (state, action) => {
                state.loading = false
                state.permissionList = action.payload
            })
            .addCase(getPermissionData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
    },
})

export const { setCompanyData, setBranchData, setRoleData, setAddonData } = dataSlice.actions

export default dataSlice.reducer
